// @ts-nocheck
class DynamicHamburgerMenu {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    const trigger = root.querySelector('sheet-trigger');
    const sheetId = trigger?.dataset.sheetTarget;
    this.sheet = sheetId ? document.getElementById(sheetId) : null;
    this.init();
  }

  init() {
    if (!this.sheet) return;

    const { signal } = this.abortController;
    this.sheet.addEventListener('click', this.handleClick, { signal });
    this.sheet.addEventListener('sheet:close', this.handleSheetClose, {
      signal,
    });
    this.sheet.addEventListener('sheet:open', this.handleSheetOpen, { signal });
  }

  destroy() {
    this.abortController.abort();
  }

  handleSheetOpen = () => {
    const firstToggle = this.sheet.querySelector(
      '[data-mobile-dropdown-toggle]'
    );
    if (firstToggle instanceof HTMLElement) {
      firstToggle.focus();
    }
  };

  handleSheetClose = () => {
    this.closeAllMobileDropdowns();
  };

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const dropdownToggle = target.closest('[data-mobile-dropdown-toggle]');
    if (dropdownToggle && this.sheet.contains(dropdownToggle)) {
      event.preventDefault();
      this.toggleMobileDropdown(dropdownToggle);
      return;
    }

    const nestedToggle = target.closest('[data-mobile-nested-dropdown-toggle]');
    if (nestedToggle && this.sheet.contains(nestedToggle)) {
      event.preventDefault();
      this.toggleMobileNestedDropdown(nestedToggle);
    }
  };

  toggleMobileDropdown(toggle) {
    const submenu = toggle.nextElementSibling;
    if (!(submenu instanceof HTMLElement)) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeMobileDropdown(toggle, submenu);
    } else {
      this.openMobileDropdown(toggle, submenu);
    }
  }

  openMobileDropdown(toggle, submenu) {
    const toggles = this.sheet.querySelectorAll(
      '[data-mobile-dropdown-toggle]'
    );
    toggles.forEach((otherToggle) => {
      if (otherToggle === toggle) return;
      const otherSubmenu = otherToggle.nextElementSibling;
      if (!(otherSubmenu instanceof HTMLElement)) return;
      this.closeMobileDropdown(otherToggle, otherSubmenu);
    });

    toggle.setAttribute('aria-expanded', 'true');
    submenu.setAttribute('aria-hidden', 'false');
    toggle.classList.add('is-open');
    submenu.classList.add('is-open');
  }

  closeMobileDropdown(toggle, submenu) {
    toggle.setAttribute('aria-expanded', 'false');
    submenu.setAttribute('aria-hidden', 'true');
    toggle.classList.remove('is-open');
    submenu.classList.remove('is-open');
  }

  closeAllMobileDropdowns() {
    const toggles = this.sheet.querySelectorAll(
      '[data-mobile-dropdown-toggle]'
    );
    toggles.forEach((toggle) => {
      const submenu = toggle.nextElementSibling;
      if (!(submenu instanceof HTMLElement)) return;
      this.closeMobileDropdown(toggle, submenu);
    });

    this.closeAllMobileNestedDropdowns();
  }

  toggleMobileNestedDropdown(toggle) {
    const nestedSubmenu = toggle.nextElementSibling;
    if (!(nestedSubmenu instanceof HTMLElement)) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeMobileNestedDropdown(toggle, nestedSubmenu);
    } else {
      this.openMobileNestedDropdown(toggle, nestedSubmenu);
    }
  }

  openMobileNestedDropdown(toggle, nestedSubmenu) {
    const parentSubmenu = toggle.closest('.hamburger-menu__submenu-inner');
    if (parentSubmenu) {
      const siblingToggles = parentSubmenu.querySelectorAll(
        '[data-mobile-nested-dropdown-toggle]'
      );
      siblingToggles.forEach((otherToggle) => {
        if (otherToggle === toggle) return;
        const otherNestedSubmenu = otherToggle.nextElementSibling;
        if (!(otherNestedSubmenu instanceof HTMLElement)) return;
        this.closeMobileNestedDropdown(otherToggle, otherNestedSubmenu);
      });
    }

    toggle.setAttribute('aria-expanded', 'true');
    nestedSubmenu.setAttribute('aria-hidden', 'false');
    toggle.classList.add('is-open');
    nestedSubmenu.classList.add('is-open');
  }

  closeMobileNestedDropdown(toggle, nestedSubmenu) {
    toggle.setAttribute('aria-expanded', 'false');
    nestedSubmenu.setAttribute('aria-hidden', 'true');
    toggle.classList.remove('is-open');
    nestedSubmenu.classList.remove('is-open');
  }

  closeAllMobileNestedDropdowns() {
    const toggles = this.sheet.querySelectorAll(
      '[data-mobile-nested-dropdown-toggle]'
    );
    toggles.forEach((toggle) => {
      const nestedSubmenu = toggle.nextElementSibling;
      if (!(nestedSubmenu instanceof HTMLElement)) return;
      this.closeMobileNestedDropdown(toggle, nestedSubmenu);
    });
  }
}

/** @type {WeakMap<HTMLElement, DynamicHamburgerMenu>} */
const hamburgerMenuInstances = new WeakMap();

/**
 * @param {ParentNode} [context]
 */
function initDynamicHamburgerMenus(context = document) {
  const roots = context.querySelectorAll('[data-dynamic-hamburger-menu]');
  roots.forEach((root) => {
    if (!(root instanceof HTMLElement)) return;

    const existing = hamburgerMenuInstances.get(root);
    if (existing) {
      existing.destroy();
    }

    hamburgerMenuInstances.set(root, new DynamicHamburgerMenu(root));
  });
}

/**
 * @param {Event} event
 */
function initHamburgerMenusFromEditorEvent(event) {
  if (!event?.target || !(event.target instanceof Element)) {
    initDynamicHamburgerMenus();
    return;
  }

  const root = event.target.closest('[data-dynamic-hamburger-menu]');
  if (root) {
    initDynamicHamburgerMenus(root.parentElement || document);
    return;
  }

  const sheet = event.target.closest('sheet-content.hamburger-menu-sheet');
  if (sheet instanceof HTMLElement && sheet.id) {
    const trigger = document.querySelector(
      `sheet-trigger[data-sheet-target="${sheet.id}"]`
    );
    const blockRoot = trigger?.closest('[data-dynamic-hamburger-menu]');
    if (blockRoot) {
      initDynamicHamburgerMenus(blockRoot.parentElement || document);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initDynamicHamburgerMenus();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event?.target instanceof HTMLElement) {
    initDynamicHamburgerMenus(event.target);
    return;
  }

  initDynamicHamburgerMenus();
});

document.addEventListener(
  'shopify:block:select',
  initHamburgerMenusFromEditorEvent
);
